import {
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent,
  type RefObject,
  type TdHTMLAttributes,
} from "react";
import {
  type GridCustomCellProps,
  type GridHandle,
  type GridRowClickEvent,
} from "@progress/kendo-react-grid";

// Kendo always sets aria-selected on every gridcell, even when the grid has
// no selection feature enabled, which makes screen readers announce
// "not selected" on every cell regardless of column. None of our tables use
// Kendo's row/cell selection, so the attribute is never meaningful here.
export function stripAriaSelected(
  tdProps?: TdHTMLAttributes<HTMLTableCellElement> | null
): TdHTMLAttributes<HTMLTableCellElement> | undefined {
  if (!tdProps) return tdProps ?? undefined;
  const { "aria-selected": _ariaSelected, ...rest } = tdProps;
  return rest;
}

// Default cell renderer used for columns that don't provide their own
// custom cell, so every column (not just ones with a custom cell) gets
// aria-selected stripped from its <td>.
export function DefaultDataCell(props: GridCustomCellProps) {
  return <td {...stripAriaSelected(props.tdProps)}>{props.children}</td>;
}

// Kendo's `navigatable` grid mode moves cell focus with the arrow keys, but
// only wires Enter/Space to column-header sorting, not to onRowClick. That
// leaves keyboard users with no way to activate a row that's only clickable
// with a mouse. This gives back a cell renderer that also fires onRowClick
// when Enter is pressed on a focused cell, so it behaves like a mouse click.
// Returns undefined when the grid has no onRowClick, so callers can fall
// back to their normal cell renderer unchanged.
export function useEnterActivatableDataCell(
  onRowClick: ((event: GridRowClickEvent) => void) | undefined,
  gridRef: RefObject<GridHandle | null>
): ComponentType<GridCustomCellProps> | undefined {
  // A ref (not the component itself) tracks the latest onRowClick so the
  // cell component's identity stays stable across renders. Kendo remounts
  // every cell for a column whenever its cell component reference changes,
  // which would drop focus/filter state on each keystroke otherwise.
  const onRowClickRef = useRef(onRowClick);
  onRowClickRef.current = onRowClick;

  const [Cell] = useState<ComponentType<GridCustomCellProps>>(
    () =>
      function EnterActivatableDataCell(props: GridCustomCellProps) {
        const tdProps = stripAriaSelected(props.tdProps);
        const handleKeyDown = (e: KeyboardEvent<HTMLTableCellElement>) => {
          tdProps?.onKeyDown?.(e);
          if (e.defaultPrevented || e.key !== "Enter") return;
          onRowClickRef.current?.({
            dataItem: props.dataItem,
            syntheticEvent: e,
            nativeEvent: e.nativeEvent,
            target: gridRef.current as GridHandle,
          });
        };
        return (
          <td {...tdProps} onKeyDown={handleKeyDown}>
            {props.children}
          </td>
        );
      }
  );

  return onRowClick ? Cell : undefined;
}
